import React, { useState } from "react"
import { Button, Checkbox, Input, Modal, Radio } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import type { LabelChange } from "../../model/shownLabels"
import { SPEC_DIALOG_Z_INDEX } from "../Header/SpecHeader.component"
import { planReplace, ReplaceOutcome, ReplaceRequest } from "./findReplace"
import type { LabelRow } from "./labelRows"

const SCOPES: { value: ReplaceRequest["scope"]; label: string }[] = [
  { value: "all", label: "Variables and categories" },
  { value: "variables", label: "Variables" },
  { value: "items", label: "Categories, rows and cells" },
]

const NOTHING = "Nothing in the labels listed matches."
/** The preview lists this many changes (and refusals), then says how many more. */
const PREVIEW_LIMIT = 200

const count = (n: number): string => n.toLocaleString("en-US")

/** A row by its raw label (the plan holds variables and items only). */
const rawOf = (row: LabelRow): string => (row.kind === "heading" ? row.text : row.raw)

const changeLine = (c: ReplaceOutcome["changes"][number]): string =>
  c.change.shown === null
    ? `${rawOf(c.row)}: "${c.now}" goes back to "${c.after}"`
    : `${rawOf(c.row)}: "${c.now}" becomes "${c.after}"`

const More: React.FC<{ shown: number; total: number }> = ({ shown, total }) =>
  total > shown ? <p className="ts-labels__more">and {count(total - shown)} more.</p> : null

export interface ReplacePanelProps {
  /** The rows the drawer lists (search and "Changed only" applied): the replace reaches only them. */
  listed: LabelRow[]
  /** Every row, for the names a result must not clash with. */
  rows: LabelRow[]
  /** Writes every change of the preview in one call. */
  onReplace: (changes: LabelChange[]) => void
  /** Where the preview dialog renders (inside the spec, for its tokens). */
  getContainer: () => HTMLElement
}

/**
 * Find and replace over the labels listed: plain text (every occurrence, the
 * replacement as typed) or a pattern (a regular expression, `$1` works), in
 * any case when asked, over the variables, their items, or both. Preview
 * opens a dialog with every change and every result left out and why;
 * Replace writes the changes in one go.
 */
const ReplacePanel: React.FC<ReplacePanelProps> = ({ listed, rows, onReplace, getContainer }) => {
  const [find, setFind] = useState("")
  const [replace, setReplace] = useState("")
  const [ignoreCase, setIgnoreCase] = useState(false)
  const [pattern, setPattern] = useState(false)
  const [scope, setScope] = useState<ReplaceRequest["scope"]>("all")
  /** The plan shown in the preview dialog, open while set. */
  const [outcome, setOutcome] = useState<ReplaceOutcome | null>(null)
  /** Why the preview did not open: the pattern cannot be read, or nothing matches. */
  const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null)

  // A change of the request drops what was said of the last one.
  const edit =
    <T,>(set: (v: T) => void) =>
    (v: T): void => {
      set(v)
      setMessage(null)
    }

  const preview = (): void => {
    const planned = planReplace(listed, rows, { find, replace, ignoreCase, pattern, scope })
    if (planned.error) setMessage({ error: true, text: planned.error })
    else if (planned.changes.length === 0 && planned.refused.length === 0)
      setMessage({ error: false, text: NOTHING })
    else {
      setMessage(null)
      setOutcome(planned)
    }
  }

  const confirm = (): void => {
    if (outcome && outcome.changes.length > 0) onReplace(outcome.changes.map((c) => c.change))
    setOutcome(null)
  }

  const changes = outcome?.changes ?? []
  const refused = outcome?.refused ?? []

  return (
    <div className="ts-labels__replace-form">
      <div className="ts-labels__replace-fields">
        <label className="ts-labels__replace-field">
          <span>Find</span>
          <Input
            size="small"
            aria-label="Find"
            value={find}
            onChange={(e) => edit(setFind)(e.target.value)}
          />
        </label>
        <label className="ts-labels__replace-field">
          <span>Replace with</span>
          <Input
            size="small"
            aria-label="Replace with"
            value={replace}
            onChange={(e) => edit(setReplace)(e.target.value)}
          />
        </label>
      </div>
      <div className="ts-labels__replace-options">
        <Checkbox checked={ignoreCase} onChange={(e) => edit(setIgnoreCase)(e.target.checked)}>
          Ignore case
        </Checkbox>
        <Checkbox checked={pattern} onChange={(e) => edit(setPattern)(e.target.checked)}>
          Use a pattern
        </Checkbox>
        <Radio.Group
          size="small"
          aria-label="Replace in"
          value={scope}
          options={SCOPES}
          onChange={(e) => edit(setScope)(e.target.value as ReplaceRequest["scope"])}
        />
      </div>
      <p className="ts-hint">Applies to the labels listed below.</p>
      <div className="ts-labels__replace-actions">
        <Button size="small" disabled={!find} onClick={preview}>
          Preview
        </Button>
        {message &&
          (message.error ? (
            <span className="ts-error" role="alert">
              {message.text}
            </span>
          ) : (
            <span className="ts-hint" role="status">
              {message.text}
            </span>
          ))}
      </div>
      {/* Over the drawer, in the platform's buttons: the way back first. */}
      <Modal
        open={outcome !== null}
        title={
          changes.length === 1 ? "Replace in 1 label" : `Replace in ${count(changes.length)} labels`
        }
        onCancel={() => setOutcome(null)}
        getContainer={getContainer}
        zIndex={SPEC_DIALOG_Z_INDEX}
        destroyOnHidden
        footer={[
          <DataQButton key="cancel" type="secondary" onClick={() => setOutcome(null)}>
            Cancel
          </DataQButton>,
          <DataQButton
            key="replace"
            type="primary"
            isDisabled={changes.length === 0}
            onClick={confirm}
          >
            Replace
          </DataQButton>,
        ]}
      >
        <div className="ts-labels__preview">
          {changes.length > 0 && (
            <>
              <ul aria-label="Changes">
                {changes.slice(0, PREVIEW_LIMIT).map((c) => (
                  <li key={c.row.key}>{changeLine(c)}</li>
                ))}
              </ul>
              <More shown={PREVIEW_LIMIT} total={changes.length} />
            </>
          )}
          {refused.length > 0 && (
            <>
              <p className="ts-labels__preview-head">Left as they are:</p>
              <ul aria-label="Left as they are">
                {refused.slice(0, PREVIEW_LIMIT).map((r) => (
                  <li key={r.row.key}>{`${rawOf(r.row)}: ${r.reason}`}</li>
                ))}
              </ul>
              <More shown={PREVIEW_LIMIT} total={refused.length} />
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default ReplacePanel
